/**
 * groupBasketView: what the Order together screens show for one basket, read
 * straight from the server's basket object (group-baskets-contract.md,
 * section 4).
 *
 * The server is the single source of truth. Nothing here adds up money,
 * splits a fee, works out a share or moves a deadline:
 *   - `nextAction` decides what the screen leads with;
 *   - `permissions` decide which controls exist (a control is shown only when
 *     its flag is true);
 *   - amounts and statuses are displayed as returned.
 * The one piece of arithmetic is the countdown, which only displays the
 * server's own deadline against the server's own clock (`serverNow`).
 */
import { CURRENCY, CURRENCY_LOCALE, formatCurrency } from "../config/market";
import { deliveryLabel, deliveryTone } from "./orderDisplay";
import { isCancelled } from "./orderStatus";

export const NEXT_ACTION = Object.freeze({
  ADD_ITEMS: "ADD_ITEMS",
  WAIT_FOR_LOCK: "WAIT_FOR_LOCK",
  CHECKOUT: "CHECKOUT",
  WAIT_FOR_HOST: "WAIT_FOR_HOST",
  PAY_SHARE: "PAY_SHARE",
  SHARE_PENDING: "SHARE_PENDING",
  WAIT_FOR_OTHERS: "WAIT_FOR_OTHERS",
  WAIT_FOR_PENDING_SHARES: "WAIT_FOR_PENDING_SHARES",
  PAY_REMAINDER: "PAY_REMAINDER",
  PREPARING_ORDER: "PREPARING_ORDER",
  TRACK_ORDER: "TRACK_ORDER",
  REFUNDING: "REFUNDING",
  CANCELLED: "CANCELLED",
});

export const MODE = Object.freeze({
  HOST_PAYS_ALL: "HOST_PAYS_ALL",
  EACH_PAYS_SHARE: "EACH_PAYS_SHARE",
});

export const MODE_LABELS = Object.freeze({
  HOST_PAYS_ALL: "I pay for everyone",
  EACH_PAYS_SHARE: "Everyone pays their share",
});

/* The mode as a member reads it (the host chose it). */
export const modeSummary = (basket) =>
  basket?.mode === MODE.HOST_PAYS_ALL
    ? `${basket?.you?.isHost ? "You pay" : `${basket?.hostLabel || "The host"} pays`} for everyone`
    : "Everyone pays their share";

export const LOCKED_STATES = Object.freeze([
  "LOCKED",
  "COLLECTING",
  "AWAITING_HOST",
  "FUNDED",
  "RELEASING",
  "RELEASED",
]);

const OPEN_STATES = ["OPEN", "TOP_UP"];

export const STATE_LABELS = Object.freeze({
  OPEN: "Open",
  TOP_UP: "Below minimum",
  LOCKED: "Locked",
  COLLECTING: "Collecting payments",
  AWAITING_HOST: "Waiting for the host",
  FUNDED: "Paid",
  RELEASING: "Sending order",
  RELEASED: "Order placed",
  CANCELLED: "Cancelled",
});

export const stateLabel = (state) => STATE_LABELS[state] || "Open";

export const SHARE_STATUS_LABELS = Object.freeze({
  UNPAID: "Not paid",
  PENDING: "Confirming",
  PAID: "Paid",
  LAPSED: "Missed",
  DROPPED: "Dropped",
  REFUND_PENDING: "Refund in progress",
  REFUNDED: "Refunded",
});

export const shareStatusLabel = (status) => SHARE_STATUS_LABELS[status] || "Not paid";

/* "done" green, "wait" amber, "stopped" grey, "todo" neutral. */
export const shareStatusTone = (status) => {
  switch (status) {
    case "PAID":
    case "REFUNDED":
      return "done";
    case "PENDING":
    case "REFUND_PENDING":
      return "wait";
    case "LAPSED":
    case "DROPPED":
      return "stopped";
    default:
      return "todo";
  }
};

export const CANCELLATION_TEXT = Object.freeze({
  HOST_CANCELLED: "The host cancelled this basket.",
  EMPTY_AT_CUTOFF: "Nobody added items before the basket closed.",
  QUOTE_FAILED: "We could not price delivery for this basket.",
  VENDOR_CLOSED: "The restaurant closed before the order went in.",
  BELOW_MINIMUM: "The basket did not reach the minimum order.",
  HOST_CHECKOUT_MISSED: "The host did not check out in time.",
  REMAINDER_MISSED: "The host did not finish paying in time.",
  RELEASE_REFUSED: "The restaurant could not take this order.",
});

/* The server's own sentence first; ours only when it sent none. */
export const cancellationText = (cancellation) =>
  cancellation?.message ||
  CANCELLATION_TEXT[cancellation?.reason] ||
  "This basket was cancelled.";

export const DEADLINE_LABELS = Object.freeze({
  CUTOFF: "Basket closes in",
  TOP_UP: "Top-up ends in",
  HOST_CHECKOUT: "Checkout closes in",
  MEMBER_PAY: "Pay within",
  PAYMENT_GRACE: "Confirming payments",
  HOST_PAY: "Host pays within",
});

export const deadlineLabel = (kind) => DEADLINE_LABELS[kind] || "Time left";

/**
 * Milliseconds until a server deadline, on the server's clock: `serverNow`
 * was true at `receivedAt` (this browser's clock when the basket arrived),
 * and only the time since then is added. Never below zero.
 */
export const remainingMs = ({ at, serverNow, receivedAt }, now = Date.now()) => {
  const deadline = Date.parse(at);
  const server = Date.parse(serverNow);
  if (!Number.isFinite(deadline)) return null;
  const serverNowAtNow = Number.isFinite(server)
    ? server + Math.max(0, now - (Number(receivedAt) || now))
    : now;
  return Math.max(0, deadline - serverNowAtNow);
};

/* 754000 -> "12:34"; an hour or more -> "1:02:05". */
export const formatCountdown = (ms) => {
  const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
};

/* The cut-off choices inside the server's window (availability.windows). */
const CUTOFF_STEPS = [10, 15, 20, 30, 45, 60, 90, 120];

export const cutoffChoices = (window) => {
  const min = Number(window?.min) || 10;
  const max = Number(window?.max) || 120;
  const fallback = Number(window?.default) || 30;
  const picks = new Set(CUTOFF_STEPS.filter((m) => m >= min && m <= max));
  if (fallback >= min && fallback <= max) picks.add(fallback);
  if (!picks.size) picks.add(fallback);
  return [...picks].sort((a, b) => a - b);
};

/* 30 -> "30 min", 60 -> "1 hr", 90 -> "1 hr 30 min". */
export const formatMinutes = (minutes) => {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  const hours = Math.floor(m / 60);
  const rest = m % 60;
  if (!hours) return `${rest} min`;
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
};

/* ── The caller ─────────────────────────────────────────────────────────── */

export const isHostOf = (basket) => Boolean(basket?.you?.isHost);

export const myMemberOf = (basket) =>
  (basket?.members || []).find((member) => member?.isYou) ||
  (basket?.members || []).find((member) => member?.memberId === basket?.you?.memberId) ||
  null;

export const myShareOf = (basket) =>
  (basket?.shares || []).find((share) => share?.memberId === basket?.you?.memberId) || null;

/* The status of the payment this browser made: the caller's share, the
   host's remainder, or (checkout) none on the basket. */
export const contributionStatusOf = (basket, role) => {
  if (role === "share") return myShareOf(basket)?.status || null;
  if (role === "remainder") return basket?.remainder?.status || null;
  return null;
};

/**
 * What one basket change says about the payment this browser started:
 * "paid", "declined" (back to UNPAID after confirming, with the server's
 * reason), "closed" (lapsed, dropped or refunded) or null (still open).
 */
export const contributionOutcome = (role, previous, next) => {
  const before = contributionStatusOf(previous, role);
  const after = contributionStatusOf(next, role);
  if (!after || after === before) return null;
  if (after === "PAID") return { outcome: "paid" };
  if (before === "PENDING" && after === "UNPAID") {
    const reason = role === "share" ? myShareOf(next)?.lastFailureReason : null;
    return { outcome: "declined", reason: reason || null };
  }
  if (["LAPSED", "DROPPED", "REFUND_PENDING", "REFUNDED"].includes(after)) return { outcome: "closed" };
  return null;
};

export const shareOfMember = (basket, memberId) =>
  (basket?.shares || []).find((share) => share?.memberId === memberId) || null;

/* "Tom", "Tom and Sarah", "Tom, Sarah and Ali": whose payment is in flight. */
export const joinNames = (names) => {
  const list = names.filter(Boolean);
  if (list.length <= 1) return list[0] || "";
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
};

export const pendingPayerNames = (basket) =>
  joinNames(
    (basket?.shares || [])
      .filter((share) => share?.status === "PENDING")
      .map((share) => share.label)
  );

/* ── What the screen leads with ─────────────────────────────────────────── */

/**
 * The headline for the basket's `nextAction`, in plain words.
 * @returns {{ title: string, detail: string, tone: "act"|"wait"|"done"|"stopped" }}
 */
export const leadOf = (basket) => {
  const host = basket?.hostLabel || "The host";
  const youHost = isHostOf(basket);
  switch (basket?.nextAction) {
    case NEXT_ACTION.ADD_ITEMS:
      if (basket?.state === "TOP_UP") {
        return {
          title: "Add items to reach the minimum",
          detail: "Only you can add items now. Lock again once the basket reaches the minimum.",
          tone: "act",
        };
      }
      return youHost
        ? {
            title: "Add your items",
            detail: "Share the link so friends add theirs. Lock the basket when everyone is in.",
            tone: "act",
          }
        : {
            title: "Add your items",
            detail: `Choose what you want before the basket closes. ${host} places one order for everyone.`,
            tone: "act",
          };
    case NEXT_ACTION.WAIT_FOR_LOCK:
      return {
        title: `Waiting for ${host} to reach the minimum`,
        detail: "The basket is below the minimum order. Only the host can add items now.",
        tone: "wait",
      };
    case NEXT_ACTION.CHECKOUT:
      return {
        title: "Check out for everyone",
        detail: "The items and total are fixed. Choose how to pay and place the order.",
        tone: "act",
      };
    case NEXT_ACTION.WAIT_FOR_HOST:
      return {
        title: `${host} is completing the order`,
        detail: "You will see the order status here once it is placed.",
        tone: "wait",
      };
    case NEXT_ACTION.PAY_SHARE:
      return {
        title: "Pay your share",
        detail: "Shares are fixed. The order goes to the restaurant once everyone has paid.",
        tone: "act",
      };
    case NEXT_ACTION.SHARE_PENDING:
      return {
        title: "Confirming your payment",
        detail: "Approve the prompt on your phone if you have not yet. Do not pay again.",
        tone: "wait",
      };
    case NEXT_ACTION.WAIT_FOR_OTHERS:
      return {
        title: "Waiting for everyone to pay",
        detail: "The order goes to the restaurant once every share is paid.",
        tone: "wait",
      };
    case NEXT_ACTION.WAIT_FOR_PENDING_SHARES: {
      const names = pendingPayerNames(basket);
      return {
        title: names ? `Waiting for ${names} to confirm` : "Waiting for payments to confirm",
        detail: "A payment is still being confirmed. You can pay what is left once it settles.",
        tone: "wait",
      };
    }
    case NEXT_ACTION.PAY_REMAINDER:
      return {
        title: "Pay what is left",
        detail: "Some shares were not paid. Pay the rest, or drop the items of members who did not pay.",
        tone: "act",
      };
    case NEXT_ACTION.PREPARING_ORDER:
      return {
        title: "Sending your order to the restaurant",
        detail: "Every amount is collected. This takes a few seconds.",
        tone: "wait",
      };
    case NEXT_ACTION.TRACK_ORDER:
      return {
        title: "Your order is placed",
        detail: youHost
          ? "One rider brings everything to your drop-off."
          : `One rider brings everything to ${host}'s drop-off.`,
        tone: "done",
      };
    case NEXT_ACTION.REFUNDING:
      return {
        title: "Refunding paid shares",
        detail: cancellationText(basket?.cancellation),
        tone: "stopped",
      };
    case NEXT_ACTION.CANCELLED:
      return {
        title: "This basket was cancelled",
        detail: cancellationText(basket?.cancellation),
        tone: "stopped",
      };
    default:
      return { title: stateLabel(basket?.state), detail: "", tone: "wait" };
  }
};

/* ── Controls ───────────────────────────────────────────────────────────── */

export const CONTROL = Object.freeze({
  ADD_ITEMS: "addItems",
  LOCK: "lock",
  CHECKOUT: "checkout",
  PAY_SHARE: "payShare",
  PAY_REMAINDER: "payRemainder",
  OPEN_ORDER: "openOrder",
  LEAVE: "leave",
  CANCEL: "cancel",
});

/* Each control and the permission that allows it. */
const CONTROL_FLAGS = [
  [CONTROL.ADD_ITEMS, "canEditLines"],
  [CONTROL.LOCK, "canLock"],
  [CONTROL.CHECKOUT, "canCheckout"],
  [CONTROL.PAY_SHARE, "canPayShare"],
  [CONTROL.PAY_REMAINDER, "canPayRemainder"],
];

/* The controls each nextAction leads with, in order. */
const LEAD_CONTROLS = {
  [NEXT_ACTION.ADD_ITEMS]: [CONTROL.ADD_ITEMS, CONTROL.LOCK],
  [NEXT_ACTION.CHECKOUT]: [CONTROL.CHECKOUT],
  [NEXT_ACTION.PAY_SHARE]: [CONTROL.PAY_SHARE],
  [NEXT_ACTION.PAY_REMAINDER]: [CONTROL.PAY_REMAINDER],
};

/* While a payment is being confirmed nothing on screen may invite another. */
const PAYMENT_CONTROLS = [CONTROL.PAY_SHARE, CONTROL.PAY_REMAINDER, CONTROL.CHECKOUT];
const CONFIRMING = [NEXT_ACTION.SHARE_PENDING, NEXT_ACTION.WAIT_FOR_PENDING_SHARES];

/**
 * The controls to render. Every one exists only when the server's permission
 * flag is true; `nextAction` only orders them.
 *
 * @returns {{ primary: string[], more: string[], quiet: string[] }}
 *   primary  what the screen leads with
 *   more     other permitted actions
 *   quiet    leave and cancel, shown small at the end
 */
export const basketControls = (basket) => {
  const permissions = basket?.permissions || {};
  const allowed = CONTROL_FLAGS.filter(([, flag]) => permissions[flag] === true).map(
    ([control]) => control
  );
  const lead = LEAD_CONTROLS[basket?.nextAction] || [];
  const primary = lead.filter((control) => allowed.includes(control));
  const confirming = CONFIRMING.includes(basket?.nextAction);
  const more = allowed.filter(
    (control) =>
      !primary.includes(control) && !(confirming && PAYMENT_CONTROLS.includes(control))
  );
  if (
    basket?.nextAction === NEXT_ACTION.TRACK_ORDER &&
    isHostOf(basket) &&
    basket?.order?.orderId
  ) {
    primary.push(CONTROL.OPEN_ORDER);
  }
  const quiet = [];
  if (permissions.canLeave === true) quiet.push(CONTROL.LEAVE);
  if (permissions.canCancel === true) quiet.push(CONTROL.CANCEL);
  return { primary, more, quiet };
};

export const MEMBER_CONTROL = Object.freeze({
  REMOVE: "remove",
  DROP: "drop",
});

/* Shares the host may drop in AWAITING_HOST (contract 5.7). */
const DROPPABLE_SHARE = ["UNPAID", "LAPSED"];

/**
 * What the caller may do to one member's row: remove them (host, OPEN) or
 * drop their unpaid items (host, AWAITING_HOST). Never themselves, never the
 * host.
 */
export const memberControls = (basket, member) => {
  const permissions = basket?.permissions || {};
  if (!member || member.isHost || member.isYou) return [];
  if (member.memberId === basket?.you?.memberId) return [];
  if (permissions.canRemoveMembers === true) return [MEMBER_CONTROL.REMOVE];
  if (permissions.canDropUnpaidMembers === true) {
    const share = shareOfMember(basket, member.memberId);
    if (share && DROPPABLE_SHARE.includes(share.status)) return [MEMBER_CONTROL.DROP];
  }
  return [];
};

/*
 * "Paid shares will be refunded" is asked for once any share is paid (or
 * being confirmed, which may yet be paid): the host's cancel then carries the
 * explicit confirmation (`confirmRefunds`). The server has the last word and
 * answers 409 REFUND_CONFIRMATION_REQUIRED when the screen did not know.
 */
export const REFUND_CONFIRMATION_TEXT = "Paid shares will be refunded";
export const REFUND_CONFIRMATION_REQUIRED = "REFUND_CONFIRMATION_REQUIRED";

const MONEY_IN = ["PAID", "PENDING"];

export const cancelNeedsRefundConfirmation = (basket) =>
  (basket?.shares || []).some((share) => MONEY_IN.includes(share?.status)) ||
  MONEY_IN.includes(basket?.remainder?.status);

/* ── Lines ──────────────────────────────────────────────────────────────── */

const optionIdsOf = (options) =>
  (options || [])
    .map((option) => String(option?._id || option || ""))
    .filter(Boolean);

const sameChoices = (a, b) =>
  optionIdsOf(a.options).sort().join("|") === optionIdsOf(b.options).sort().join("|") &&
  JSON.stringify(a.selectedSpecs || {}) === JSON.stringify(b.selectedSpecs || {});

/* One line as PUT /lines takes it: ids, quantity and choices, never a price. */
export const linePayload = (line) => ({
  productId: String(line.productId),
  qty: Number(line.qty) || 1,
  options: optionIdsOf(line.options).map((_id) => ({ _id })),
  selectedSpecs: line.selectedSpecs || {},
});

/* The caller's lines as the server holds them, ready to send back. */
export const myLinesPayload = (basket) => (myMemberOf(basket)?.lines || []).map(linePayload);

export const MAX_LINE_QTY = 99;

/**
 * The caller's lines with one saved: `replaceLineId` replaces that line; a
 * new line with the same dish and the same choices as an existing one adds
 * to its quantity instead of repeating it.
 */
export const linesWithSaved = (basket, line, replaceLineId = null) => {
  const mine = myMemberOf(basket)?.lines || [];
  const next = linePayload(line);
  if (replaceLineId) {
    return mine.map((existing) =>
      existing.lineId === replaceLineId ? next : linePayload(existing)
    );
  }
  const match = mine.find(
    (existing) => String(existing.productId) === next.productId && sameChoices(existing, next)
  );
  if (!match) return [...mine.map(linePayload), next];
  return mine.map((existing) =>
    existing === match
      ? { ...linePayload(existing), qty: Math.min(MAX_LINE_QTY, (Number(existing.qty) || 0) + next.qty) }
      : linePayload(existing)
  );
};

export const linesWithout = (basket, lineId) =>
  (myMemberOf(basket)?.lines || []).filter((line) => line.lineId !== lineId).map(linePayload);

export const UNPRICED_TEXT = Object.freeze({
  SOLD_OUT: "Sold out. Removed when the basket locks.",
  PRICE_ON_REQUEST: "Price on request. Removed when the basket locks.",
  NOT_FOUND: "No longer on the menu. Removed when the basket locks.",
  NOT_SOLD_HERE: "Not sold at this branch. Removed when the basket locks.",
});

export const unpricedText = (reason) =>
  UNPRICED_TEXT[reason] || "Cannot be ordered. Removed when the basket locks.";

const UNPRICED_SHORT = Object.freeze({
  SOLD_OUT: "sold out",
  PRICE_ON_REQUEST: "price on request",
  NOT_FOUND: "no longer on the menu",
  NOT_SOLD_HERE: "not sold at this branch",
});

/* "Mango juice (Tom), sold out": a line the lock removed. */
export const removedLineText = (line) =>
  `${line?.name} (${line?.label}), ${UNPRICED_SHORT[line?.reason] || "could not be ordered"}`;

/* ── Money, as the server sent it ───────────────────────────────────────── */

/* A server amount in the basket's own currency (major units, as sent). */
export const formatBasketMoney = (amount, currency = CURRENCY) => {
  if (!currency || currency === CURRENCY) return formatCurrency(amount);
  const n = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  try {
    return new Intl.NumberFormat(CURRENCY_LOCALE, { style: "currency", currency }).format(n);
  } catch (_) {
    return `${currency} ${n}`;
  }
};

/* One line of what the remainder covers: "Tom (UGX 27,333)". */
export const coverText = (money) => (cover) =>
  `${cover.label}${cover.dropped ? ", items dropped" : ""} (${money(cover.amount)})`;

/* The minimum, for the progress bar. The fill is a proportion, not money. */
export const minimumProgressOf = (pricing) => {
  const minimumOrder = pricing?.minimumOrder;
  const minimum = Number(minimumOrder?.minimum) || 0;
  if (!minimumOrder || minimum <= 0) return null;
  const subtotal = Number(minimumOrder.subtotal) || 0;
  return {
    minimum,
    subtotal,
    shortfall: Number(minimumOrder.shortfall) || 0,
    met: minimumOrder.met === true,
    fill: Math.max(0, Math.min(1, subtotal / minimum)),
  };
};

/* The order after release, in the existing order tracking words. */
export const orderSummaryOf = (order) => {
  if (!order) return null;
  const cancelled = isCancelled(order.status) || isCancelled(order.deliveryStatus);
  const asOrder = { delivery_status: order.deliveryStatus };
  return {
    orderId: order.orderId || null,
    label: cancelled ? "Cancelled" : deliveryLabel(asOrder),
    tone: cancelled ? "danger" : deliveryTone(asOrder),
    placedAt: order.placedAt || null,
  };
};

export const isOpenBasket = (basket) => OPEN_STATES.includes(basket?.state);
export const isLockedBasket = (basket) => LOCKED_STATES.includes(basket?.state);

/* ── Analytics transitions ──────────────────────────────────────────────── */

/**
 * The growth events one basket change proves, seen by comparing the basket
 * on screen with the one that replaced it. Only real transitions count (a
 * basket opened already cancelled is not a cancellation), and the basket-wide
 * events are sent from the host's browser only, so ten members do not count
 * one lock ten times. `shareRail` is the rail this browser paid the caller's
 * share on, when it did.
 *
 * @returns {Array<{ name: string, params: object }>}
 */
export const transitionEvents = (previous, next, { shareRail = null } = {}) => {
  if (!previous || !next || previous.id !== next.id) return [];
  const events = [];
  const host = isHostOf(next);
  const members = (next.members || []).length;
  if (host && OPEN_STATES.includes(previous.state) && LOCKED_STATES.includes(next.state)) {
    events.push({ name: "group_basket_locked", params: { members } });
  }
  if (host && previous.state !== "RELEASED" && next.state === "RELEASED") {
    events.push({ name: "group_order_placed", params: { members, mode: next.mode } });
  }
  if (host && previous.state !== "CANCELLED" && next.state === "CANCELLED") {
    events.push({
      name: "group_basket_cancelled",
      params: { reason: next.cancellation?.reason || "UNKNOWN" },
    });
  }
  const before = myShareOf(previous)?.status;
  const after = myShareOf(next)?.status;
  if (shareRail && before !== "PAID" && after === "PAID") {
    events.push({ name: "group_share_paid", params: { rail: shareRail } });
  }
  return events;
};

/** Quantities of the caller's saved items, combining variants of each product. */
export const productQuantitiesOf = (lines = []) => {
  const quantities = new Map();
  for (const line of lines || []) {
    const productId = String(line.productId?._id || line.productId || "");
    const qty = Number(line.qty);
    if (!productId || line.dropped || !Number.isFinite(qty) || qty <= 0) continue;
    quantities.set(productId, (quantities.get(productId) || 0) + qty);
  }
  return quantities;
};
